import { formSlugs } from "./form-constants";

// C-Tribe x Film 2027 application forms (film/submission, film/pitch,
// film/industry). Spec: SaffaBrain 01-projects/ctribe-festival-main-site/
// film-application-forms-research-2026-10.md. The legacy `film` form keeps
// `filmGenreOptions` in form-constants.ts so existing responses still render.

export const FILM_FESTIVAL_DATES = "August 12-17, 2027";
export const FILM_OPENS_ON = "December 1, 2026";
export const FILM_EARLY_DEADLINE = "February 1, 2027";
export const FILM_REGULAR_DEADLINE = "March 15, 2027";
export const FILM_FINAL_DEADLINE = "April 30, 2027";
export const FILM_SELECTION_DATE = "June 4, 2027";
export const FILM_LINEUP_DATE = "June 24, 2027";
export const FILM_INDUSTRY_CLOSES_ON = "July 15, 2027";

export const filmFormLinks = {
  submission: "/form/film/submission",
  pitch: "/form/film/pitch",
  industry: "/form/film/industry",
  mediaPress: "/form/media-press",
} as const;

// 3.2 Age and guardian
export const filmAgeOptions = [
  "I am 18 or older",
  "I am under 18 and a parent or guardian has filled in the section below",
] as const;
export const filmGuardianConsentOptions = [
  "I am the parent or legal guardian of this applicant. I have read this form and I consent to this application and to the terms below on their behalf.",
] as const;
export const filmIndustryAgeOptions = ["Yes, I am 18 or older"] as const;

// 3.3 How did you hear about us
export const filmHeardAboutOptions = [
  "Instagram", "TikTok", "LinkedIn", "Facebook", "Google search", "Email from C-Tribe",
  "Friend or colleague", "Film school or organisation", "Another festival", "Press", "Other",
] as const;

// 3.4 Ticket estimate and sharing (submission and pitch only)
export const filmNetworkTurnoutOptions = ["0-10", "11-25", "26-50", "51-100", "100+"] as const;
export const filmShareChannelOptions = [
  "Instagram", "TikTok", "Email list", "Community groups", "Press", "Other",
] as const;

// 4.1 Submission
export const filmSubmitterRoleOptions = [
  "Director", "Producer", "Writer", "Distributor or sales agent", "Other",
] as const;
export const filmFormatOptions = [
  "Feature (50 minutes or longer)", "Short (under 50 minutes)", "Episodic or web series", "Music video",
] as const;
export const filmTypeOptions = ["Narrative", "Documentary", "Hybrid"] as const;
export const filmStyleOptions = [
  "Animation", "Experimental", "Student film", "First film by the director",
] as const;
export const filmGenreChoices = [
  "Drama", "Comedy", "Thriller", "Horror", "Sci-fi or fantasy", "Romance", "Action",
  "Family", "Music", "Social issue", "Arts and culture", "Sports", "Other",
] as const;
export const filmFinalCutOptions = ["Yes, final cut", "No, near-final work in progress"] as const;
export const filmYesNoOptions = ["Yes", "No"] as const;
export const filmSubtitleOptions = [
  "Yes", "Not needed, all dialogue is in English", "Not yet, we can add them by July 9, 2027", "No",
] as const;
export const filmPremiereStatusOptions = [
  "Nowhere yet (world premiere)",
  "Only in its home country (international premiere available)",
  "Outside Canada but not in Canada (Canadian premiere available)",
  "In Canada but not in Alberta",
  "In Alberta but not in Edmonton",
  "Already shown in Edmonton",
] as const;
export const filmPublicOnlineOptions = ["Yes", "No", "Geo-blocked or festival platform only"] as const;
export const filmDistributionStatusOptions = [
  "Yes, worldwide", "Yes, in some territories", "No, and we are looking", "No, and we plan to self-distribute",
] as const;
export const filmTeamIdentityOptions = [
  "First-time director", "Indigenous", "Black", "Racialised or person of colour", "Woman",
  "Gender-diverse", "2SLGBTQIA+", "Person with a disability", "Newcomer to Canada", "Prefer not to say",
] as const;
export const filmAttendanceOptions = ["Yes", "Maybe", "No, but we could join by video"] as const;
export const filmAiUseOptions = ["No", "Yes (tell us how below)"] as const;
export const filmExhibitionFormatOptions = [
  "DCP", "ProRes 422 HQ file", "H.264 or H.265 file (1080p or higher)", "Not sure yet",
] as const;
export const filmSoundOptions = ["Stereo", "5.1", "7.1", "Other"] as const;
export const filmCaptionOptions = ["Yes", "Not yet, we can create one if selected", "No"] as const;
export const filmContentAdvisoryOptions = [
  "Violence", "Sexual content", "Nudity", "Coarse language", "Substance use",
  "Suicide or self-harm", "Flashing lights", "None",
] as const;
export const filmRightsHolderOptions = [
  "Yes, I hold the rights",
  "Yes, I am authorised by the rights holder",
  "No (stop: we cannot accept the film)",
] as const;
export const filmMusicClearedOptions = [
  "Yes, all music is original or licensed for festival exhibition", "Clearance is in progress", "No",
] as const;

// 4.2 Pitch
export const filmPitchRoleOptions = [
  "Director", "Producer", "Writer", "Writer-director", "Showrunner", "Other",
] as const;
export const filmPitchTrackOptions = [
  "Finished film looking for distribution or sales",
  "Project looking for partners or financing",
] as const;
export const filmProjectFormatOptions = [
  "Feature film", "Short film", "Documentary feature", "Documentary short",
  "Series (scripted)", "Series (unscripted or docuseries)", "Other",
] as const;
export const filmRightsAvailableOptions = [
  "Worldwide", "Canada", "United States", "Other territories", "Broadcast", "Streaming", "Theatrical", "Educational",
] as const;
export const filmProjectStageOptions = [
  "Idea or treatment", "Script in development", "Financing", "Pre-production", "Production", "Post-production or rough cut",
] as const;
export const filmBudgetRangeOptions = [
  "Under $50,000", "$50,000-$250,000", "$250,001-$1,000,000", "$1,000,001-$3,000,000", "Over $3,000,000", "Not budgeted yet",
] as const;
export const filmFinancingSecuredOptions = [
  "None yet", "Under 10%", "10-25%", "26-50%", "51-75%", "Over 75%",
] as const;
export const filmFinancingSourceOptions = [
  "Telefilm Canada", "Canada Media Fund", "Provincial agency or tax credit", "Broadcaster or streamer",
  "Distributor", "Private investment", "Arts council grant", "Crowdfunding", "Self-funded", "Other",
] as const;
export const filmSeekingOptions = [
  "Distribution (Canada)", "Distribution (international)", "Sales agent", "Broadcaster or streamer",
  "Co-producer", "Executive producer", "Financing or investment", "Grants and public funding advice",
  "Festival strategy advice", "Mentorship",
] as const;
export const filmPartnerTypeOptions = [
  "Distributors", "Sales agents", "Broadcasters and streamers", "Producers", "Public funders",
  "Private investors", "Agents and managers", "Festival programmers",
] as const;
export const filmAttendInPersonOptions = ["Yes", "Maybe", "No, online only"] as const;
export const filmMeetingDayOptions = [
  "Thursday, August 12", "Friday, August 13", "Saturday, August 14",
  "Sunday, August 15", "Monday, August 16", "Tuesday, August 17",
] as const;

// 4.3 Industry
export const filmOrganizationTypeOptions = [
  "Distributor", "Sales agent", "Broadcaster or streamer", "Production company", "Public funder or agency",
  "Private investor or fund", "Talent agency or management", "Festival or programmer", "Film commission", "Other",
] as const;
export const filmDecisionRoleOptions = [
  "Yes, I make or recommend these decisions",
  "I scout and report to a decision-maker",
  "No, I am attending to learn and network",
] as const;
export const filmLookingForOptions = [
  "Finished features to acquire", "Finished shorts", "Documentaries", "Series", "Projects to co-produce",
  "Projects to finance", "Writers or directors to represent", "Films for festival programming", "Talent for hire",
] as const;
export const filmFormatsOfInterestOptions = [
  "Feature", "Short", "Documentary", "Scripted series", "Unscripted series", "Music video", "Animation",
] as const;
export const filmMeetingCapacityOptions = ["1-5", "6-10", "11-20", "20+"] as const;
export const filmMeetingModeOptions = ["In person only", "Online only", "Either"] as const;
export const filmPanelInterestOptions = ["Yes", "Maybe", "No"] as const;

// Section 7 legal copy. Counsel review is pending for 7.1 and 7.4.
const link = (href: string, label: string) =>
  `<a href="${href}" class="text-blue-500" target="_blank" rel="noopener noreferrer">${label}</a>`;
const termsLink = link("https://www.ctribefestival.com/terms", "Terms of Service");
const privacyLink = link("https://www.ctribefestival.com/privacy", "Privacy Policy");

export const filmPrivacyAcknowledgement = `C-Tribe Society collects the information on this form to review your application, run the festival and contact you about it. Our programming team and jurors see your submission. If you are selected for pitch meetings, a short project profile (title, logline, format, stage, what you are looking for, and your name) is shared with registered industry participants, and your full materials only with those you agree to meet. Optional identity questions are used for programming priorities and anonymous totals, never published. We keep applications for 3 years, then delete them. Questions or requests to see or delete your information: hello@ctribefestival.com. Full details in our ${privacyLink}.`;

export const filmRightsLicence = `By submitting, I confirm that I own this film or am authorised by the rights holder to submit it, and that the music, footage, artwork and other material in it are cleared for public festival screenings, or I have told C-Tribe what is still outstanding.<br/><br/>If the film is selected and I confirm the selection, I grant C-Tribe Society a non-exclusive, royalty-free licence to: (a) screen the film to audiences at C-Tribe Festival venues in Edmonton between August 12 and 17, 2027, up to the number of screenings stated in the selection letter; (b) use the title, synopsis, credits, stills, the trailer and excerpts of up to 2 minutes in festival guides, on C-Tribe websites and social media, in press materials and in festival advertising, until December 31, 2028. I keep all ownership. Any online screening needs my separate written approval.<br/><br/>Submitting does not guarantee selection. C-Tribe is not responsible for loss of or damage to submitted materials. I have read the ${termsLink} and ${privacyLink}.`;

export const filmPitchMaterialsConsent = "I confirm I have the right to share these materials and to pitch this project. I agree that C-Tribe may share my project profile with registered industry participants and share my full materials with those who accept a meeting with me. C-Tribe does not guarantee meetings, deals or funding, is not a party to any agreement I make with an industry participant, and does not take a fee or share of any deal. Industry participants are asked to keep materials confidential, but C-Tribe cannot enforce that, so please do not include anything you are not willing to share.";

export const filmIndustryTerms = `I confirm I am 18 or older and the information above is accurate. I will use filmmaker materials only to assess projects for my organisation, will not share them outside it without the filmmaker's permission, and will treat meeting requests respectfully. C-Tribe may remove registrants who misuse materials or contact filmmakers in a way that breaks the C-Tribe code of conduct. I have read the ${termsLink} and ${privacyLink}.`;

// 7.2: optional, single option, never pre-selected.
export const filmMarketingOptInOptions = [
  "Yes, send me C-Tribe x Film and C-Tribe Festival news, calls for submissions and ticket offers by email. C-Tribe Society, Edmonton, Alberta, hello@ctribefestival.com. You can unsubscribe at any time.",
] as const;

// 4.2 pitch track branching. Mirrors selectVillageVendorFields: only the
// chosen track's questions are shown, and the other track's answers are
// cleared when the track is saved. Unknown tracks unlock neither branch.
export const filmPitchTrackFields: Record<(typeof filmPitchTrackOptions)[number], readonly string[]> = {
  [filmPitchTrackOptions[0]]: [
    "runtimeMinutes", "completionMonth", "screenerUrl", "screenerPassword",
    "premiereStatus", "rightsAvailable", "salesStatus",
  ],
  [filmPitchTrackOptions[1]]: [
    "projectStage", "budgetRange", "financingSecured", "financingSources",
    "financePlan", "statusReport", "projectedCompletion",
  ],
};

function activePitchTrackFields(track: unknown): readonly string[] {
  return typeof track === "string" && Object.hasOwn(filmPitchTrackFields, track)
    ? filmPitchTrackFields[track as keyof typeof filmPitchTrackFields]
    : [];
}

export function selectFilmPitchFields<T>(fields: Record<string, T>, track: unknown): Record<string, T> {
  const active = activePitchTrackFields(track);
  const hidden = new Set(Object.values(filmPitchTrackFields).flat().filter((key) => !active.includes(key)));
  return Object.fromEntries(Object.entries(fields).filter(([key]) => !hidden.has(key)));
}

export function clearInactiveFilmPitchAnswers(track: unknown): Record<string, null> {
  const active = activePitchTrackFields(track);
  return Object.fromEntries(
    Object.values(filmPitchTrackFields).flat().filter((key) => !active.includes(key)).map((key) => [key, null])
  );
}

function titleOf(answers: Record<string, unknown> | null | undefined, key: string): string {
  const value = answers?.[key];
  return typeof value === "string" && value.trim() ? value.trim() : "your project";
}

export function getFilmConfirmation(slug: string, answers?: Record<string, unknown> | null): string | undefined {
  if (slug === formSlugs.filmSubmission) {
    const title = titleOf(answers, "filmTitle");
    return `Thanks. We have your submission for ${title === "your project" ? "your film" : title}. Our programming team reviews every film. We will email you by ${FILM_SELECTION_DATE}. Please keep your screener link live until August 31, 2027.`;
  }
  if (slug === formSlugs.filmPitch) {
    return `Thanks. We have your pitch application for ${titleOf(answers, "projectTitle")}. We will email you by ${FILM_SELECTION_DATE}. If you are selected, you will be able to request meetings from June 7, 2027.`;
  }
  if (slug === formSlugs.filmIndustry) {
    return `Thanks for registering for C-Tribe x Film industry meetings. We review each registration before sharing filmmaker profiles, and we will email you with next steps. Registration closes ${FILM_INDUSTRY_CLOSES_ON}.`;
  }
  return undefined;
}
